import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtCOP } from "@/lib/format";
import { Plane, Building2, Mountain, Clock3, Loader2 } from "lucide-react";

const TYPES = [
  { id: "aeropuerto", label: "Aeropuerto", icon: Plane },
  { id: "ciudad", label: "Ciudad", icon: Building2 },
  { id: "fuera_ciudad", label: "Fuera ciudad", icon: Mountain },
  { id: "por_horas", label: "Por horas", icon: Clock3 },
];

const empty = {
  name: "", whatsapp: "", email: "",
  service_type: "aeropuerto",
  origin: "Bogotá", destination: "",
  date: "", time: "", flight_number: "", passengers: 5,
  total_hours: "", notes: "",
  website: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [routes, setRoutes] = useState([]);

  useEffect(() => { api.get("/routes").then((r) => setRoutes(Array.isArray(r.data) ? r.data : [])).catch(() => setRoutes([])); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Nombre demasiado corto";
    const wa = form.whatsapp.replace(/[^0-9]/g, "");
    if (wa.length < 7) e.whatsapp = "WhatsApp inválido";
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Email inválido";
    if (!form.date) e.date = "Elige una fecha";
    else {
      const d = new Date(form.date + "T00:00:00");
      const today = new Date(); today.setHours(0,0,0,0);
      if (d < today) e.date = "La fecha ya pasó";
    }
    if (!form.origin.trim()) e.origin = "Requerido";
    if (form.service_type !== "ciudad" && form.service_type !== "por_horas" && !form.destination.trim()) e.destination = "Requerido";
    if (form.service_type === "por_horas" && (!form.total_hours || Number(form.total_hours) < 1)) e.total_hours = "Ingresa las horas";
    if (form.service_type === "aeropuerto") {
      if (!form.time) e.time = "Hora requerida para traslado aeropuerto";
      if (!form.flight_number.trim()) e.flight_number = "Número de vuelo requerido";
    }
    if (!form.passengers || Number(form.passengers) < 1) e.passengers = "Mínimo 1";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Revisa los campos marcados"); return; }
    setLoading(true);
    try {
      const payload = { ...form,
        passengers: Number(form.passengers),
        total_hours: form.total_hours ? Number(form.total_hours) : null,
        email: form.email || null,
        destination: form.destination || null,
        time: form.time || null,
        flight_number: form.flight_number || null,
        website: form.website || "",
      };
      await api.post("/quotes", payload);
      const okMsg = form.email
        ? "¡Solicitud enviada! Revisa tu email — te contactaremos por WhatsApp en breve."
        : "¡Solicitud enviada! Te contactaremos por WhatsApp en breve.";
      toast.success(okMsg);
      setForm(empty); setErrors({});
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) toast.error("Recibimos muchas solicitudes desde tu red. Intenta de nuevo en unos minutos.");
      else toast.error(err?.response?.data?.detail || "Error al enviar");
    } finally { setLoading(false); }
  };

  const matchedRoute = Array.isArray(routes)
    ? routes.find((r) => r?.destination && form.destination && r.destination.toLowerCase() === form.destination.toLowerCase())
    : null;

  const inputCls = (k) => `w-full bg-[#171412] border rounded-lg px-4 py-3 text-[#F5EFE0] placeholder-[#F5EFE0]/40 focus:border-[#D4AF37] outline-none ${errors[k] ? "border-rose-500/70" : "border-[#3A3229]"}`;

  return (
    <section id="cotizador" data-testid="quote-section" className="py-24 md:py-32 relative">
      <div className="orb-gold w-[500px] h-[500px] right-[-140px] top-[100px] opacity-50" />
      <div className="max-w-6xl mx-auto px-5 md:px-8 relative">
        <div className="grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-2">
            <div className="text-[#E8C87A] text-xs uppercase tracking-[0.35em] mb-4">Cotizador</div>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-[#F5EFE0]">
              Recibe tu cotización <span className="italic text-gold-grad">en minutos</span>
            </h2>
            <p className="text-[#F5EFE0]/65 mt-4 text-base leading-relaxed">
              Cuéntanos sobre tu viaje y te enviamos una propuesta por WhatsApp. Sin costo, sin compromiso.
            </p>
            <div className="mt-6 p-5 card-lux">
              <div className="text-xs uppercase tracking-widest text-[#D4AF37]">¿Prefieres hablar directo?</div>
              <a href="https://wa.me/573123616674" target="_blank" rel="noreferrer" className="mt-2 inline-block font-display text-2xl text-[#F5EFE0] hover:text-[#E8C87A]"
                 style={{ transitionProperty: "color", transitionDuration: "200ms" }}>
                +57 312 361 6674
              </a>
              <div className="text-xs text-[#F5EFE0]/50 mt-1">Lunes a Domingo · 6:00 a.m. – 10:00 p.m.</div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="quote-form" className="lg:col-span-3 card-lux p-6 md:p-8 space-y-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Tipo de servicio</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = form.service_type === t.id;
                  return (
                    <button
                      key={t.id} type="button"
                      data-testid={`service-type-${t.id}`}
                      onClick={() => set("service_type", t.id)}
                      className={`flex flex-col items-center gap-2 py-3 rounded-lg border text-xs font-medium ${active ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F5EFE0]" : "border-[#3A3229] text-[#F5EFE0]/70 hover:border-[#D4AF37]/60"}`}
                      style={{ transitionProperty: "border-color,background-color", transitionDuration: "200ms" }}
                    >
                      <Icon size={18} className={active ? "text-[#E8C87A]" : "text-[#F5EFE0]/60"} />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Nombre completo</label>
                <input data-testid="q-name" className={inputCls("name")} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Ana Martínez" />
                {errors.name && <div className="text-xs text-rose-400 mt-1">{errors.name}</div>}
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">WhatsApp</label>
                <input data-testid="q-whatsapp" className={inputCls("whatsapp")} value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+57 300 123 4567" />
                {errors.whatsapp && <div className="text-xs text-rose-400 mt-1">{errors.whatsapp}</div>}
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Email (opcional)</label>
                <input data-testid="q-email" className={inputCls("email")} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ana@correo.com" />
                {errors.email && <div className="text-xs text-rose-400 mt-1">{errors.email}</div>}
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Pasajeros</label>
                <input data-testid="q-passengers" type="number" min="1" className={inputCls("passengers")} value={form.passengers} onChange={(e) => set("passengers", e.target.value)} />
                {errors.passengers && <div className="text-xs text-rose-400 mt-1">{errors.passengers}</div>}
              </div>
              <div>
                <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Origen</label>
                <input data-testid="q-origin" className={inputCls("origin")} value={form.origin} onChange={(e) => set("origin", e.target.value)} placeholder="Bogotá" />
                {errors.origin && <div className="text-xs text-rose-400 mt-1">{errors.origin}</div>}
              </div>
              {form.service_type !== "ciudad" && form.service_type !== "por_horas" && (
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Destino</label>
                  <input data-testid="q-destination" className={inputCls("destination")} value={form.destination} onChange={(e) => set("destination", e.target.value)} placeholder="Villa de Leyva" list="route-suggestions" />
                  <datalist id="route-suggestions">
                    {(Array.isArray(routes) ? routes : []).map((r) => <option key={r.id} value={r.destination} />)}
                  </datalist>
                  {errors.destination && <div className="text-xs text-rose-400 mt-1">{errors.destination}</div>}
                  {matchedRoute && (
                    <div className="text-xs text-[#E8C87A] mt-1.5" data-testid="q-price-hint">
                      Referencia: desde {fmtCOP(matchedRoute.price_from)}
                    </div>
                  )}
                </div>
              )}
              {form.service_type === "por_horas" && (
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Horas totales</label>
                  <input data-testid="q-hours" type="number" min="1" className={inputCls("total_hours")} value={form.total_hours} onChange={(e) => set("total_hours", e.target.value)} placeholder="8" />
                  {errors.total_hours && <div className="text-xs text-rose-400 mt-1">{errors.total_hours}</div>}
                </div>
              )}
              {form.service_type === "aeropuerto" && (
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Número de vuelo <span className="text-[#E8C87A]">*</span></label>
                  <input data-testid="q-flight" className={inputCls("flight_number")} value={form.flight_number} onChange={(e) => set("flight_number", e.target.value.toUpperCase())} placeholder="AV123 · LA4567" maxLength={20} />
                  {errors.flight_number && <div className="text-xs text-rose-400 mt-1">{errors.flight_number}</div>}
                </div>
              )}
              <div>
                <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Fecha</label>
                <input data-testid="q-date" type="date" className={inputCls("date")} value={form.date} onChange={(e) => set("date", e.target.value)} />
                {errors.date && <div className="text-xs text-rose-400 mt-1">{errors.date}</div>}
              </div>
              {form.service_type !== "fuera_ciudad" && (
                <div>
                  <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">
                    Hora {form.service_type === "aeropuerto" ? <span className="text-[#E8C87A]">*</span> : <span className="text-[#F5EFE0]/40">(opcional)</span>}
                  </label>
                  <input data-testid="q-time" type="time" className={inputCls("time")} value={form.time} onChange={(e) => set("time", e.target.value)} />
                  {errors.time && <div className="text-xs text-rose-400 mt-1">{errors.time}</div>}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-[#F5EFE0]/60 mb-1.5 block">Notas (opcional)</label>
              <textarea data-testid="q-notes" className={inputCls("notes") + " min-h-[90px] resize-none"} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Equipaje adicional, paradas intermedias, etc." />
            </div>

            <button data-testid="q-submit" type="submit" disabled={loading} className="btn-gold w-full py-3.5 rounded-full text-[15px] disabled:opacity-70 flex items-center justify-center gap-2">
              {loading && <Loader2 size={16} className="animate-spin" />}
              {loading ? "Enviando…" : "Enviar solicitud"}
            </button>
            {/* Honeypot: hidden from users, visible to bots that auto-fill everything */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>Website (deja vacío)
                <input type="text" tabIndex={-1} autoComplete="off" value={form.website || ""} onChange={(e) => set("website", e.target.value)} />
              </label>
            </div>
            <div className="text-[11px] text-[#F5EFE0]/40 text-center">Tus datos son confidenciales · Respuesta en menos de 30 min</div>
          </form>
        </div>
      </div>
    </section>
  );
}
